import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Play, Save, TrendingUp, TrendingDown, Activity, Target } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';

const BacktestPage = () => {
  const { api, user } = useAuth();
  const [strategies, setStrategies] = useState([]);
  const [backtests, setBacktests] = useState([]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('run');

  const [form, setForm] = useState({
    symbol: '',
    startDate: '',
    endDate: '',
    initialCapital: 10000,
    strategyType: 'moving_average',
    shortPeriod: 50,
    longPeriod: 200,
    oversold: 30,
    overbought: 70
  });

  useEffect(() => {
    loadStrategies();
    loadHistory();
  }, []);

  const loadStrategies = async () => {
    try {
      const res = await api.get('/backtest/strategies');
      setStrategies(res.data);
    } catch (error) {
      console.error('Failed to load strategies:', error);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await api.get('/backtest/results');
      setBacktests(res.data);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const runBacktest = async () => {
    if (!form.symbol || !form.startDate || !form.endDate) {
      alert('Please fill in all fields');
      return;
    }

    setRunning(true);
    try {
      const res = await api.post('/backtest/run', {
        symbol: form.symbol,
        startDate: form.startDate,
        endDate: form.endDate,
        initialCapital: form.initialCapital,
        strategy: {
          type: form.strategyType,
          shortPeriod: form.shortPeriod,
          longPeriod: form.longPeriod,
          oversold: form.oversold,
          overbought: form.overbought
        }
      });
      setResult(res.data);
      loadHistory();
    } catch (error) {
      console.error('Backtest failed:', error);
      alert(error.response?.data?.error || 'Backtest failed');
    } finally {
      setRunning(false);
    }
  };

  const saveStrategy = async () => {
    try {
      await api.post('/backtest/strategies', {
        name: `${form.strategyType.toUpperCase()} on ${form.symbol}`,
        description: `${form.shortPeriod}/${form.longPeriod} ${form.strategyType}`,
        type: form.strategyType,
        parameters: {
          shortPeriod: form.shortPeriod,
          longPeriod: form.longPeriod,
          oversold: form.oversold,
          overbought: form.overbought
        }
      });
      loadStrategies();
      alert('Strategy saved');
    } catch (error) {
      console.error('Failed to save strategy:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    const prefix = value >= 0 ? '+' : '';
    return `${prefix}${(value || 0).toFixed(2)}%`;
  };

  if (user?.tier === 'free') {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <Card className="bg-white">
            <CardContent className="p-8">
              <Target className="w-16 h-16 mx-auto mb-4 text-[#1A4D2E]" />
              <h1 className="font-serif text-2xl text-[#1F2937] mb-4">Backtesting Requires Pro</h1>
              <p className="text-gray-600 mb-6">
                Upgrade to Pro to access strategy backtesting and test your trading strategies with historical data.
              </p>
              <Button className="bg-[#1A4D2E] hover:bg-[#143d24]">Upgrade to Pro</Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-serif text-3xl text-[#1F2937]">Strategy Backtesting</h1>
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            Pro Feature
          </Badge>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="run">Run Backtest</TabsTrigger>
            <TabsTrigger value="strategies">My Strategies</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="run">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle>Configuration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Symbol</label>
                    <Input
                      value={form.symbol}
                      onChange={e => setForm({...form, symbol: e.target.value.toUpperCase()})}
                      placeholder="AAPL"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Start Date</label>
                      <Input
                        type="date"
                        value={form.startDate}
                        onChange={e => setForm({...form, startDate: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">End Date</label>
                      <Input
                        type="date"
                        value={form.endDate}
                        onChange={e => setForm({...form, endDate: e.target.value})}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Initial Capital</label>
                    <Input
                      type="number"
                      value={form.initialCapital}
                      onChange={e => setForm({...form, initialCapital: parseFloat(e.target.value)})}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Strategy Type</label>
                    <Select
                      value={form.strategyType}
                      onValueChange={v => setForm({...form, strategyType: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="moving_average">Moving Average Crossover</SelectItem>
                        <SelectItem value="rsi">RSI Strategy</SelectItem>
                        <SelectItem value="bollinger">Bollinger Bands</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {form.strategyType === 'moving_average' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Short Period</label>
                        <Input
                          type="number"
                          value={form.shortPeriod}
                          onChange={e => setForm({...form, shortPeriod: parseInt(e.target.value)})}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Long Period</label>
                        <Input
                          type="number"
                          value={form.longPeriod}
                          onChange={e => setForm({...form, longPeriod: parseInt(e.target.value)})}
                        />
                      </div>
                    </div>
                  )}

                  {form.strategyType === 'rsi' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Oversold</label>
                        <Input
                          type="number"
                          value={form.oversold}
                          onChange={e => setForm({...form, oversold: parseInt(e.target.value)})}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Overbought</label>
                        <Input
                          type="number"
                          value={form.overbought}
                          onChange={e => setForm({...form, overbought: parseInt(e.target.value)})}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 pt-4">
                    <Button
                      className="flex-1"
                      onClick={runBacktest}
                      disabled={running}
                    >
                      {running ? 'Running...' : <><Play className="w-4 h-4 mr-2" /> Run</>}
                    </Button>
                    <Button variant="outline" onClick={saveStrategy}>
                      <Save className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Results</CardTitle>
                </CardHeader>
                <CardContent>
                  {result ? (
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-green-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Total Return</p>
                          <p className={`text-2xl font-bold ${result.totalReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatPercent(result.totalReturn)}
                          </p>
                        </div>
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Win Rate</p>
                          <p className="text-2xl font-bold text-blue-600">{result.winRate?.toFixed(1)}%</p>
                        </div>
                        <div className="bg-yellow-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Max Drawdown</p>
                          <p className="text-2xl font-bold text-yellow-600">-{result.maxDrawdown?.toFixed(2)}%</p>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Total Trades</p>
                          <p className="text-2xl font-bold text-purple-600">{result.tradesCount}</p>
                        </div>
                      </div>

                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={result.equityCurve}>
                            <XAxis
                              dataKey="date"
                              tickFormatter={d => new Date(d).toLocaleDateString()}
                            />
                            <YAxis tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                            <Tooltip
                              formatter={(value) => [formatCurrency(value), 'Value']}
                              labelFormatter={d => new Date(d).toLocaleDateString()}
                            />
                            <Area
                              type="monotone"
                              dataKey="value"
                              stroke="#1A4D2E"
                              fill="#1A4D2E"
                              fillOpacity={0.1}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Configure and run a backtest to see results</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="strategies">
            <Card>
              <CardHeader>
                <CardTitle>Saved Strategies</CardTitle>
              </CardHeader>
              <CardContent>
                {strategies.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {strategies.map(strategy => (
                      <div key={strategy.id} className="border border-stone-200 rounded-lg p-4">
                        <h3 className="font-semibold text-[#1F2937]">{strategy.name}</h3>
                        <p className="text-sm text-gray-500 mt-1">{strategy.type}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(strategy.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No saved strategies yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Recent Backtests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-stone-200">
                        <th className="text-left py-3 text-sm font-medium text-gray-600">Symbol</th>
                        <th className="text-left py-3 text-sm font-medium text-gray-600">Return</th>
                        <th className="text-left py-3 text-sm font-medium text-gray-600">Win Rate</th>
                        <th className="text-left py-3 text-sm font-medium text-gray-600">Drawdown</th>
                        <th className="text-left py-3 text-sm font-medium text-gray-600">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backtests.map(bt => (
                        <tr key={bt.id} className="border-b border-stone-100">
                          <td className="py-3 font-medium">{bt.symbol}</td>
                          <td className={`py-3 ${parseFloat(bt.totalReturn) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatPercent(parseFloat(bt.totalReturn))}
                          </td>
                          <td className="py-3">{parseFloat(bt.winRate || 0).toFixed(1)}%</td>
                          <td className="py-3 text-yellow-600">-{parseFloat(bt.maxDrawdown || 0).toFixed(2)}%</td>
                          <td className="py-3 text-gray-500">{new Date(bt.createdAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default BacktestPage;
